import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeft, FileText, Save, Plus, Trash2, BookOpen, Loader2, Sparkles,
} from "lucide-react";
import api, { getBoardroomToken } from "../utils/qccaApi";

const EMPTY_MOTION = { text: "", mover: "", seconder: "", carried: "unknown", vote_counts: { yes: 0, no: 0, abstain: 0 } };
const EMPTY_ACTION = { text: "", owner: "", due_date: "tbd" };

export default function QCCAClerkDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    if (!getBoardroomToken()) { nav("/qcca/boardroom/login"); return; }
    api.get(`/qcca/clerk/sessions/${id}`)
      .then((r) => setSession(r.data))
      .catch(() => { toast.error("Session not found."); nav("/qcca/boardroom/clerk"); })
      .finally(() => setLoading(false));
  }, [id, nav]);

  const updateMinutes = (newMinutes) => setSession((s) => ({ ...s, minutes: newMinutes }));

  const saveMinutes = async () => {
    setSaving(true);
    try {
      await api.patch(`/qcca/clerk/sessions/${id}/minutes`, { minutes: session.minutes });
      toast.success("Edits saved.");
    } catch (e) {
      toast.error("Could not save.");
    } finally { setSaving(false); }
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const r = await api.post(`/qcca/clerk/sessions/${id}/minutes`);
      setSession((s) => ({ ...s, minutes: r.data.minutes, status: "minutes_ready" }));
      toast.success("Minutes regenerated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regeneration failed.");
    } finally { setRegenerating(false); }
  };

  const saveToMinuteBook = async () => {
    try {
      await saveMinutes();
      const r = await api.post(`/qcca/clerk/sessions/${id}/save-to-minute-book`);
      toast.success("Saved to Minute Book.");
      nav(`/qcca/boardroom/minutebook/${r.data.document.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save to Minute Book.");
    }
  };

  if (loading || !session) return <div className="min-h-screen bg-black text-gray-500 font-mono text-sm p-10">Loading…</div>;

  const m = session.minutes || null;

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/qcca/boardroom/clerk" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 hover:text-[#C5A52A] mb-6">
          <ArrowLeft className="w-3 h-3" /> Clerk
        </Link>

        {/* Header */}
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">Meeting</div>
            <h1 className="mt-2 font-headline text-4xl text-white leading-tight" data-testid="qcca-clerk-title">{session.title}</h1>
            <div className="mt-2 font-mono text-[11px] text-gray-500">
              {session.meeting_date || new Date(session.created_at).toLocaleDateString()} · {session.corporation}
            </div>
          </div>
          {m && (
            <div className="flex flex-wrap gap-2">
              <button onClick={saveMinutes} disabled={saving}
                className="inline-flex items-center gap-2 border border-[#1a1a1a] hover:border-[#C5A52A] text-gray-300 hover:text-white font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2 transition-colors"
                data-testid="qcca-clerk-save-edits">
                <Save className="w-3 h-3" /> {saving ? "Saving…" : "Save edits"}
              </button>
              <button onClick={regenerate} disabled={regenerating}
                className="inline-flex items-center gap-2 border border-[#1a1a1a] hover:border-[#C5A52A] text-gray-300 hover:text-white font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2 transition-colors"
                data-testid="qcca-clerk-regen">
                {regenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Regenerate
              </button>
              <button onClick={saveToMinuteBook}
                className="inline-flex items-center gap-2 bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2 transition-colors"
                data-testid="qcca-clerk-save-to-mb">
                <BookOpen className="w-3 h-3" /> Save to Minute Book
              </button>
            </div>
          )}
        </div>

        {/* Audio */}
        {session.audio_url && (
          <div className="mb-8 bg-[#0a0a0a] border border-[#1a1a1a] p-5 flex items-center gap-4">
            <FileText className="w-4 h-4 text-[#C5A52A]" />
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 shrink-0">Source audio</div>
            <audio controls src={`${process.env.REACT_APP_BACKEND_URL}${session.audio_url}`} className="flex-1" />
          </div>
        )}

        {/* Transcript */}
        {session.transcript && (
          <details className="mb-8 bg-[#050505] border border-[#1a1a1a]">
            <summary className="cursor-pointer p-5 font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5A52A] hover:text-white">
              Raw transcript ({session.transcript.length} chars)
            </summary>
            <div className="px-5 pb-5 font-mono text-xs text-gray-300 leading-relaxed whitespace-pre-wrap max-h-96 overflow-y-auto" data-testid="qcca-clerk-transcript">
              {session.transcript}
            </div>
          </details>
        )}

        {/* Structured minutes */}
        {!m ? (
          <div className="bg-[#0a0a0a] border border-[#1a1a1a] p-10 text-center">
            <Sparkles className="w-8 h-8 text-[#C5A52A] mx-auto" />
            <h3 className="mt-4 font-headline text-xl text-white">No minutes yet.</h3>
            <p className="mt-2 text-gray-500 text-sm">
              {session.transcript ? "Ready to draft minutes from the transcript." : "Upload and transcribe audio first."}
            </p>
          </div>
        ) : (
          <div className="space-y-8" data-testid="qcca-clerk-minutes">
            {/* Summary */}
            <Section title="Summary">
              <textarea rows={4} value={m.summary || ""}
                onChange={(e) => updateMinutes({ ...m, summary: e.target.value })}
                className="w-full bg-black border border-[#1a1a1a] focus:border-[#C5A52A] text-gray-200 text-sm px-4 py-3 outline-none resize-none" />
            </Section>

            {/* Attendees */}
            <Section title="Attendees">
              <TagEditor list={m.attendees || []} placeholder="Add name"
                onChange={(list) => updateMinutes({ ...m, attendees: list })} testid="qcca-minutes-attendees" />
            </Section>

            {/* Motions */}
            <Section title={`Motions (${(m.motions || []).length})`}
              onAdd={() => updateMinutes({ ...m, motions: [...(m.motions || []), { ...EMPTY_MOTION, vote_counts: { yes: 0, no: 0, abstain: 0 } }] })}>
              {(m.motions || []).map((mo, i) => (
                <div key={`motion-${i}-${mo.text?.substring(0, 30)}`} className="bg-black border border-[#1a1a1a] p-4 space-y-3" data-testid={`qcca-minutes-motion-${i}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5A52A]">Motion {i + 1}</div>
                    <button onClick={() => updateMinutes({ ...m, motions: m.motions.filter((_, j) => j !== i) })}
                      className="text-gray-600 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                  </div>
                  <textarea rows={2} value={mo.text || ""}
                    onChange={(e) => updateField("motions", i, "text", e.target.value, m, updateMinutes)}
                    className="w-full bg-black border border-[#1a1a1a] focus:border-[#C5A52A] text-gray-200 text-sm px-3 py-2 outline-none resize-none"
                    placeholder="Motion text" />
                  <div className="grid sm:grid-cols-3 gap-2">
                    <input value={mo.mover || ""} placeholder="Moved by"
                      onChange={(e) => updateField("motions", i, "mover", e.target.value, m, updateMinutes)}
                      className="bg-black border border-[#1a1a1a] focus:border-[#C5A52A] text-gray-200 text-sm px-3 py-2 outline-none font-mono" />
                    <input value={mo.seconder || ""} placeholder="Seconded by"
                      onChange={(e) => updateField("motions", i, "seconder", e.target.value, m, updateMinutes)}
                      className="bg-black border border-[#1a1a1a] focus:border-[#C5A52A] text-gray-200 text-sm px-3 py-2 outline-none font-mono" />
                    <select value={mo.carried || "unknown"}
                      onChange={(e) => updateField("motions", i, "carried", e.target.value, m, updateMinutes)}
                      className="bg-black border border-[#1a1a1a] focus:border-[#C5A52A] text-gray-200 text-sm px-3 py-2 outline-none font-mono"
                      data-testid={`qcca-minutes-motion-${i}-carried`}>
                      <option value="carried">Carried</option>
                      <option value="not_carried">Not carried</option>
                      <option value="unknown">Unknown</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {["yes", "no", "abstain"].map((k) => (
                      <div key={k} className="flex items-center gap-2">
                        <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500 w-12">{k}</label>
                        <input type="number" min="0" value={mo.vote_counts?.[k] ?? 0}
                          onChange={(e) => {
                            const vc = { ...(mo.vote_counts || {}), [k]: parseInt(e.target.value || "0", 10) };
                            updateField("motions", i, "vote_counts", vc, m, updateMinutes);
                          }}
                          className="w-full bg-black border border-[#1a1a1a] focus:border-[#C5A52A] text-gray-200 text-sm px-2 py-1 outline-none font-mono" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </Section>

            {/* Action items */}
            <Section title={`Action items (${(m.action_items || []).length})`}
              onAdd={() => updateMinutes({ ...m, action_items: [...(m.action_items || []), { ...EMPTY_ACTION }] })}>
              {(m.action_items || []).map((a, i) => (
                <div key={`action-${i}-${a.text?.substring(0, 30)}`} className="bg-black border border-[#1a1a1a] p-3 grid grid-cols-12 gap-2 items-center" data-testid={`qcca-minutes-action-${i}`}>
                  <input value={a.text || ""} placeholder="What needs to happen?"
                    onChange={(e) => updateField("action_items", i, "text", e.target.value, m, updateMinutes)}
                    className="col-span-6 bg-black border border-[#1a1a1a] focus:border-[#C5A52A] text-gray-200 text-sm px-3 py-2 outline-none" />
                  <input value={a.owner || ""} placeholder="Owner"
                    onChange={(e) => updateField("action_items", i, "owner", e.target.value, m, updateMinutes)}
                    className="col-span-3 bg-black border border-[#1a1a1a] focus:border-[#C5A52A] text-gray-200 text-sm px-3 py-2 outline-none font-mono" />
                  <input value={a.due_date || ""} placeholder="Due (YYYY-MM-DD or tbd)"
                    onChange={(e) => updateField("action_items", i, "due_date", e.target.value, m, updateMinutes)}
                    className="col-span-2 bg-black border border-[#1a1a1a] focus:border-[#C5A52A] text-gray-200 text-sm px-3 py-2 outline-none font-mono" />
                  <button onClick={() => updateMinutes({ ...m, action_items: m.action_items.filter((_, j) => j !== i) })}
                    className="col-span-1 text-gray-600 hover:text-red-400 flex justify-center"><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
            </Section>

            {/* Decisions */}
            <Section title="Decisions (non-motion)">
              <TagEditor list={m.decisions || []} placeholder="Add decision" multiline
                onChange={(list) => updateMinutes({ ...m, decisions: list })} testid="qcca-minutes-decisions" />
            </Section>

            {m._parse_error && (
              <div className="bg-red-500/5 border border-red-500/40 p-4 font-mono text-xs text-red-300">
                Auto-structuring had trouble parsing the LLM response. You can edit everything above manually, or click Regenerate.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children, onAdd }) {
  return (
    <div className="bg-[#0a0a0a] border border-[#1a1a1a] p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">{title}</div>
        {onAdd && (
          <button onClick={onAdd}
            className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 hover:text-[#C5A52A]">
            <Plus className="w-3 h-3" /> Add
          </button>
        )}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function TagEditor({ list, onChange, placeholder, multiline, testid }) {
  const [draft, setDraft] = useState("");
  return (
    <div data-testid={testid}>
      {list.length === 0 && <div className="text-gray-600 font-mono text-xs">No entries.</div>}
      <div className="space-y-2">
        {list.map((item, i) => (
          <div key={`list-${i}-${item.substring(0, 20)}`} className="flex items-center gap-2 bg-black border border-[#1a1a1a] px-3 py-2">
            {multiline ? (
              <textarea rows={1} value={item} onChange={(e) => { const n = [...list]; n[i] = e.target.value; onChange(n); }}
                className="flex-1 bg-transparent text-gray-200 text-sm outline-none resize-none" />
            ) : (
              <input value={item} onChange={(e) => { const n = [...list]; n[i] = e.target.value; onChange(n); }}
                className="flex-1 bg-transparent text-gray-200 text-sm outline-none font-mono" />
            )}
            <button onClick={() => onChange(list.filter((_, j) => j !== i))} className="text-gray-600 hover:text-red-400">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && draft.trim()) { onChange([...list, draft.trim()]); setDraft(""); } }}
          placeholder={placeholder}
          className="flex-1 bg-black border border-[#1a1a1a] focus:border-[#C5A52A] text-gray-200 text-sm px-3 py-2 outline-none font-mono" />
        <button onClick={() => { if (draft.trim()) { onChange([...list, draft.trim()]); setDraft(""); } }}
          className="bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2">Add</button>
      </div>
    </div>
  );
}

function updateField(listKey, idx, field, value, m, updateMinutes) {
  const list = [...(m[listKey] || [])];
  list[idx] = { ...list[idx], [field]: value };
  updateMinutes({ ...m, [listKey]: list });
}
